import type { CompanySize, DbcAmount, ProductInterest, Step2Data } from './validators';

export const CHILI_PIPER_DOMAIN = 'blinq';
export const CHILI_PIPER_ROUTER = 'inbound-demo-form';
export const CHILI_PIPER_TRIGGER = 'InAppButton';

const CHILI_PIPER_STATE_ATTRIBUTE = 'data-chili-piper-state';
const CHILI_PIPER_ROUTING_STATE = 'routing';
const MULTI_VALUE_SEPARATOR = ';';

// These values go through Chili Piper into HubSpot, so they must match HubSpot's enum options.
const HUBSPOT_COMPANY_SIZE: Record<CompanySize, string> = {
  '1-25': '1-25',
  '26-250': '26-250',
  '251-1000': '251-1000',
  '1000+': '1000+',
};

const HUBSPOT_PRODUCT_INTEREST: Record<ProductInterest, string> = {
  'Event Lead Capture': 'Lead Capture',
  'Digital Business Cards': 'Digital Business Cards',
};

const HUBSPOT_DBC_AMOUNT: Record<DbcAmount, string> = {
  '1-10': '1-10',
  '11-50': '11-50',
  '51-100': '51-100',
  '101-250': '101-250',
  '251-1000': '251-1000',
  '1000+': '1000+',
};

export const ChiliPiperFieldNames = {
  email: 'companyemail',
  firstName: 'firstname',
  lastName: 'lastname',
  jobTitle: 'jobtitle',
  companyName: 'companyname',
  companySize: 'companysize',
  region: 'demoregionselected',
  productInterest: 'productinterest',
  eventAttendance: 'eventattendance',
  dbcAmount: 'dbcamount',
  hearAboutUs: 'howdidyouhearaboutblinq',
  phone: 'phone',
} as const;

// Concierge is published as a UMD global (no ESM build), so we wrap the `window.ChiliPiper`
// surface with a typed declaration rather than importing it. The callback/option names
// match what concierge.js actually listens for — `onSuccess` fires on `booking-confirmed`
// and `closePopup()` is the only way to dismiss the modal (there's no `closeOnSuccess` or
// `closeDelay`; the modal stays open until something calls `closePopup`).
type ChiliPiperSubmitOptions = {
  trigger: typeof CHILI_PIPER_TRIGGER;
  lead: Record<string, string>;
  closeOnOutside?: boolean;
  onRouting?: () => void;
  onRouted?: () => void;
  onSuccess?: () => void;
  onError?: (error?: unknown) => void;
  onClose?: () => void;
};

type ChiliPiperGlobal = {
  submit: (domain: string, router: string, options: ChiliPiperSubmitOptions) => void;
  closePopup: () => void;
};

declare global {
  interface Window {
    ChiliPiper?: ChiliPiperGlobal;
  }
}

export function buildChiliPiperLead(formData: Step2Data): Record<string, string> {
  return {
    [ChiliPiperFieldNames.email]: formData.email,
    [ChiliPiperFieldNames.firstName]: formData.firstName,
    [ChiliPiperFieldNames.lastName]: formData.lastName,
    [ChiliPiperFieldNames.jobTitle]: formData.jobTitle,
    [ChiliPiperFieldNames.companyName]: formData.companyName,
    [ChiliPiperFieldNames.companySize]: HUBSPOT_COMPANY_SIZE[formData.companySize],
    [ChiliPiperFieldNames.region]: formData.region,
    [ChiliPiperFieldNames.productInterest]: formData.productInterest
      .map((interest) => HUBSPOT_PRODUCT_INTEREST[interest])
      .join(MULTI_VALUE_SEPARATOR),
    [ChiliPiperFieldNames.eventAttendance]: formData.eventAttendance ?? '',
    [ChiliPiperFieldNames.dbcAmount]: formData.dbcAmount
      ? HUBSPOT_DBC_AMOUNT[formData.dbcAmount]
      : '',
    [ChiliPiperFieldNames.hearAboutUs]: formData.hearAboutUs.join(MULTI_VALUE_SEPARATOR),
    [ChiliPiperFieldNames.phone]: '',
  };
}

export function getChiliPiperErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error;
    return typeof message === 'string' ? message : JSON.stringify(message);
  }

  return 'Booking is unavailable right now. Please refresh and try again.';
}

export function hideChiliPiperFrameUntilRouted() {
  document.body.setAttribute(CHILI_PIPER_STATE_ATTRIBUTE, CHILI_PIPER_ROUTING_STATE);
}

export function showChiliPiperFrame() {
  if (document.body.getAttribute(CHILI_PIPER_STATE_ATTRIBUTE) === CHILI_PIPER_ROUTING_STATE) {
    document.body.removeAttribute(CHILI_PIPER_STATE_ATTRIBUTE);
  }
}
